import {
  assertPayVoxPullDateRange,
  PAYVOX_CASHBOOK_MAX_DAYS,
  type PayVoxCashbookPullPreview
} from '@finvox/shared/cashbook';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { DatePicker } from '@/components/ui/DatePicker';
import { MoneyDisplay } from '@/components/ui/MoneyDisplay';
import {
  commitPayVoxCashbookPull,
  previewPayVoxCashbookPull,
  type CashbookPermissions
} from '@/lib/cashbookFirestore';
import { resolveUserErrorMessage } from '@/lib/toastErrors';
import { useToast } from '@/contexts/ToastContext';
import { cn } from '@/lib/cn';

type Props = {
  open: boolean;
  onClose: () => void;
  orgId: string;
  periodId: string;
  uid: string;
  permissions: CashbookPermissions;
  defaultDate: string;
  busy: boolean;
  setBusy: (v: boolean) => void;
  onSuccess: () => void;
};

function translatePreviewError(t: (key: string, opts?: Record<string, unknown>) => string, msg: string): string {
  if (msg.includes('No PayVox sales in FinVox mirror')) {
    return t('cashbook.payvox.errors.notFound');
  }
  if (msg.includes('No PayVox sales in FinVox')) {
    return t('cashbook.payvox.errors.notFound');
  }
  if (msg.includes('No PayVox sales found')) return t('cashbook.payvox.errors.notFound');
  if (msg === 'All PayVox sales in this range are already imported') {
    return t('cashbook.payvox.errors.alreadyImported');
  }
  if (msg === 'No importable PayVox sales in this range') {
    return t('cashbook.payvox.errors.noneImportable');
  }
  if (msg.includes('No days can be imported')) {
    return t('cashbook.payvox.errors.blockedManual');
  }
  if (msg === 'Sales category is not configured') {
    return t('cashbook.payvox.errors.noSalesCategory');
  }
  if (msg.includes('Date range exceeds')) return t('cashbook.payvox.errors.rangeTooWide');
  if (msg === 'End date must be on or after start date') {
    return t('cashbook.payvox.errors.dateOrder');
  }
  if (msg === 'Set opening treasury first') return t('cashbook.noOpening');
  return msg;
}

export function CashbookPayVoxPullDialog({
  open,
  onClose,
  orgId,
  periodId,
  uid,
  permissions,
  defaultDate,
  busy,
  setBusy,
  onSuccess
}: Props) {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const [startDate, setStartDate] = useState(defaultDate);
  const [endDate, setEndDate] = useState(defaultDate);
  const [preview, setPreview] = useState<PayVoxCashbookPullPreview | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setStartDate(defaultDate);
    setEndDate(defaultDate);
    setPreview(null);
    setApiError(null);
  }, [open, defaultDate]);

  if (!open) return null;

  const payload = {
    orgId,
    periodId,
    startDate,
    endDate,
    mode: 'daily_total' as const,
    locale: i18n.language
  };

  const importableCount =
    preview?.importableRows?.length ??
    preview?.rows.filter((r) => r.dayStatus === 'ok').length ??
    0;

  function validateDates(): string | null {
    if (endDate < startDate) return t('cashbook.payvox.errors.dateOrder');
    try {
      assertPayVoxPullDateRange(startDate, endDate);
    } catch {
      return t('cashbook.payvox.errors.rangeTooWide', { max: PAYVOX_CASHBOOK_MAX_DAYS });
    }
    return null;
  }

  async function runPreview() {
    const dateError = validateDates();
    if (dateError) {
      setApiError(dateError);
      setPreview(null);
      return;
    }
    setApiError(null);
    setBusy(true);
    try {
      const result = await previewPayVoxCashbookPull(orgId, payload, permissions);
      setPreview(result);
    } catch (e) {
      setApiError(resolveUserErrorMessage(e, 'cashbook.payvox.errors.failed'));
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  async function handleCommit() {
    if (importableCount === 0) return;
    const dateError = validateDates();
    if (dateError) {
      setApiError(dateError);
      return;
    }
    setBusy(true);
    setApiError(null);
    try {
      const res = await commitPayVoxCashbookPull(orgId, uid, payload, permissions);
      toast.success(
        t('cashbook.payvox.successWithBlocked', {
          count: res.imported,
          skipped: res.skippedCount,
          blocked: res.blockedCount
        }),
        'cashbook-payvox'
      );
      onSuccess();
      onClose();
    } catch (e) {
      setApiError(resolveUserErrorMessage(e, 'cashbook.payvox.errors.failed'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="cashbook-payvox-title"
    >
      <Card className="flex max-h-[90vh] w-full max-w-lg flex-col overflow-hidden p-6 shadow-lg">
        <div className="shrink-0">
          <h2 id="cashbook-payvox-title" className="text-lg font-semibold">
            {t('cashbook.payvox.title')}
          </h2>
          <p className="mt-1 text-sm text-[var(--fv-text-muted)]">{t('cashbook.payvox.hint')}</p>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <div>
              <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.payvox.fromDate')}</label>
              <DatePicker
                className="mt-1 w-full"
                value={startDate}
                onChange={(v) => {
                  setStartDate(v);
                  setPreview(null);
                  setApiError(null);
                }}
              />
            </div>
            <div>
              <label className="text-xs text-[var(--fv-text-muted)]">{t('cashbook.payvox.toDate')}</label>
              <DatePicker
                className="mt-1 w-full"
                value={endDate}
                onChange={(v) => {
                  setEndDate(v);
                  setPreview(null);
                  setApiError(null);
                }}
              />
            </div>
          </div>

          <div className="mt-4">
            <Button variant="secondary" size="sm" loading={busy} onClick={() => void runPreview()}>
              {t('cashbook.payvox.preview')}
            </Button>
          </div>

          {apiError ? (
            <p className="mt-4 rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-900">
              {apiError}
            </p>
          ) : null}
        </div>

        {preview ? (
          <div className="mt-4 min-h-0 flex-1 space-y-3 overflow-y-auto text-sm">
            <p>{t('cashbook.payvox.saleCount', { count: preview.saleCount })}</p>
            <p>
              {t('cashbook.payvox.total')}: <MoneyDisplay value={preview.totalAmount} />
            </p>
            {preview.skippedCount > 0 ? (
              <p className="text-[var(--fv-text-muted)]">
                {t('cashbook.payvox.skipped', { count: preview.skippedCount })}
              </p>
            ) : null}
            {(preview.blockedCount ?? 0) > 0 ? (
              <p className="text-[var(--fv-text-muted)]">
                {t('cashbook.payvox.blockedCount', { count: preview.blockedCount })}
              </p>
            ) : null}
            {preview.errors.length > 0 ? (
              <ul className="max-h-40 overflow-y-auto rounded-lg border border-red-300 bg-red-50 p-3 text-red-900">
                {preview.errors.map((msg) => (
                  <li key={msg}>{translatePreviewError(t, msg)}</li>
                ))}
              </ul>
            ) : null}
            <div className="space-y-3">
              {preview.rows.map((row) => (
                <div
                  key={`${row.date}-${row.payvoxSaleId ?? row.payvoxDayKey}`}
                  className={cn(
                    'rounded-lg border border-[var(--fv-border)] p-3',
                    row.dayStatus === 'blocked' && 'border-amber-300 bg-amber-50',
                    row.dayStatus === 'skip' && 'opacity-70'
                  )}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium">{row.date}</span>
                    <span className="text-xs font-medium uppercase tracking-wide text-[var(--fv-text-muted)]">
                      {row.dayStatus === 'ok'
                        ? t('cashbook.payvox.statusOk')
                        : row.dayStatus === 'skip'
                          ? t('cashbook.payvox.statusSkip')
                          : t('cashbook.payvox.statusBlocked')}
                    </span>
                  </div>
                  {row.dayStatus === 'blocked' ? (
                    <p className="mt-2 text-xs text-amber-950">
                      {t('cashbook.payvox.blockedManualIncome')}
                    </p>
                  ) : null}
                  <dl className="mt-2 space-y-1 text-sm">
                    <div className="flex justify-between gap-2">
                      <dt>{t('cashbook.payvox.payvoxSales')}</dt>
                      <dd>
                        <MoneyDisplay value={row.amount} />
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2 text-[var(--fv-text-muted)]">
                      <dt>− {t('cashbook.payvox.drawerExpenses')}</dt>
                      <dd>
                        <MoneyDisplay value={row.drawerExpenses ?? '0.00'} />
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2 text-[var(--fv-text-muted)]">
                      <dt>− {t('cashbook.payvox.treasuryExpenses')}</dt>
                      <dd>
                        <MoneyDisplay value={row.treasuryExpenses ?? '0.00'} />
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2 text-[var(--fv-text-muted)]">
                      <dt>− {t('cashbook.payvox.unclassifiedExpenses')}</dt>
                      <dd>
                        <MoneyDisplay value={row.unclassifiedExpenses ?? '0.00'} />
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2 border-t border-[var(--fv-border)] pt-1 font-semibold">
                      <dt>{t('cashbook.payvox.incomeToAdd')}</dt>
                      <dd>
                        <MoneyDisplay value={row.incomeToAdd ?? '0.00'} />
                      </dd>
                    </div>
                    <div className="flex justify-between gap-2 text-xs text-[var(--fv-text-muted)]">
                      <dt>{t('cashbook.payvox.netAfterExpenses')}</dt>
                      <dd>
                        <MoneyDisplay value={row.netAfterExpenses ?? '0.00'} />
                      </dd>
                    </div>
                  </dl>
                </div>
              ))}
            </div>
          </div>
        ) : null}

        <div className="mt-6 flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="secondary" className="min-h-11 w-full sm:w-auto" onClick={onClose} disabled={busy}>
            {t('common.cancel')}
          </Button>
          <Button
            className="min-h-11 w-full sm:w-auto"
            loading={busy}
            disabled={importableCount === 0}
            onClick={() => void handleCommit()}
          >
            {t('cashbook.payvox.confirm')}
          </Button>
        </div>
      </Card>
    </div>
  );
}
